import * as React from "react";
import { cn } from "@/lib/utils";

export type BridgelayerMarkProps = React.SVGProps<SVGSVGElement> & {
  size?: number | string;
  /** Wrap the mark in a rounded-square tile (favicon / app-icon style). */
  tile?: boolean;
};

/**
 * Bridge mark: two pillars + a connecting span over a base line.
 * Strokes/fills inherit currentColor so it works on glass surfaces in
 * either theme.
 */
export const BridgelayerMark = React.forwardRef<
  SVGSVGElement,
  BridgelayerMarkProps
>(({ size = 24, tile = false, className, ...props }, ref) => {
  return (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className={cn("shrink-0", className)}
      {...props}
    >
      {tile ? (
        <rect
          x="0"
          y="0"
          width="32"
          height="32"
          rx="7"
          className="fill-[hsl(var(--brand-anchor))]"
        />
      ) : null}
      <g
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2.4"
        fill="none"
      >
        <path d="M5 22 Q16 9 27 22" />
      </g>
      <g fill="currentColor">
        <rect x="4.2" y="20" width="2.6" height="6.5" rx="0.7" />
        <rect x="25.2" y="20" width="2.6" height="6.5" rx="0.7" />
        <rect x="3" y="25.2" width="26" height="1.6" rx="0.8" opacity="0.85" />
      </g>
    </svg>
  );
});
BridgelayerMark.displayName = "BridgelayerMark";

export type BridgelayerWordmarkProps = React.HTMLAttributes<HTMLSpanElement> & {
  /** "horizontal" (mark + word) or "word" (word only). */
  variant?: "horizontal" | "word";
  size?: "sm" | "md" | "lg";
};

const wordSize: Record<NonNullable<BridgelayerWordmarkProps["size"]>, string> = {
  sm: "text-[15px]",
  md: "text-[17px]",
  lg: "text-2xl",
};

const markSize: Record<NonNullable<BridgelayerWordmarkProps["size"]>, number> = {
  sm: 18,
  md: 22,
  lg: 28,
};

/**
 * Wordmark: lowercase "bridgelayer" set in Inter Tight with tight tracking.
 * Optional bridge mark on the left.
 */
export function BridgelayerWordmark({
  variant = "horizontal",
  size = "md",
  className,
  ...props
}: BridgelayerWordmarkProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-2 select-none",
        className,
      )}
      {...props}
    >
      {variant === "horizontal" ? (
        <BridgelayerMark size={markSize[size]} />
      ) : null}
      <span
        className={cn(
          "font-display font-semibold tracking-tight leading-none",
          wordSize[size],
        )}
        style={{ letterSpacing: "-0.025em" }}
      >
        bridgelayer
      </span>
    </span>
  );
}
